import React, { useEffect, useRef } from "react";
import { ITkApprovalDateSymbolNumberRegistrationDialog } from "../../../type/componentsType/TkApprovalDateSymbolNumberRegistrationDialog";
import TkModalWithTitle from "../../atoms/TkModalWithTitle";
import { Box, Typography } from "@mui/material";
import TkFakeTableLine from "../../atoms/TkFakeTableLine";
import "./_style.scss";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import TkInputWithText from "../TkInputWithText";
import { TkSelectForm } from "../../atoms/TkSelectForm";
import { CommonOrCommonRetirementFlug } from "../../../assets/data/Constants";
import _ from "lodash";

/**
 * 承認日付記号番号登録ダイアログコンポーネント。
 * @param {ITkApprovalDateSymbolNumberRegistrationDialog} props - コンポーネントのプロパティ。
 * @returns {React.FC} 承認日付記号番号登録ダイアログコンポーネント。
 */
const TkApprovalDateSymbolNumberRegistrationDialog: React.FC<
  ITkApprovalDateSymbolNumberRegistrationDialog
> = (props) => {
  const {
    open,
    onSuccess,
    onClose,
    JobType,
    displayOnlyPaymentDeadline,
    isScheduledPaymentDateVisible,
    isScheduledPaymentDateDisabled,
    wantToSetValue = {},
  } = props;

  const [formValue, setFormValue] = React.useState<any>({});
  const [errorMsgList, setErrorMsgList] = React.useState<any>({
    symbolNumber: { message: "" },
    symbolNumberType: { message: "" },
    settlementDate: { message: "" },
    contractConclusionDate: { message: "" },
    scheduledPaymentDate: { message: "" },
    paymentDeadline: { message: "" },
  });
  const [paymentDeadlineDisabled, setPaymentDeadlineDisabled] =
    React.useState<boolean>(false);

  const previousValueRef = useRef(null);

  useEffect(() => {
    if (!_.isEqual(wantToSetValue, previousValueRef.current)) {
      setFormValue(wantToSetValue);
      previousValueRef.current = wantToSetValue;
    }
  }, [wantToSetValue]);

  const clearData = () => {
    setFormValue({});
    setErrorMsgList({
      symbolNumber: { message: "" },
      symbolNumberType: { message: "" },
      settlementDate: { message: "" },
      contractConclusionDate: { message: "" },
      scheduledPaymentDate: { message: "" },
    });
  };

  const validateForm = (formValue: any, paymentDeadlineDisabled: boolean) => {
    const {
      symbolNumber: formSymbolNumber,
      symbolNumberType: formSymbolNumberType,
      settlementDate: formSettlementDate,
      contractConclusionDate: formContractConclusionDate,
      scheduledPaymentDate: formScheduledPaymentDate,
      paymentDeadline: formPaymentDeadline,
    } = formValue;

    const currentDate = new Date();
    const minDate = new Date("1900-01-01");
    const maxDate = new Date("2099-12-31");

    const errorObj = {
      symbolNumber: {
        message: () =>
          !formSymbolNumber ? TkPropertyMessageGet("TKMSG_SCR0043E") : "",
      },
      symbolNumberType: {
        message: () =>
          !formSymbolNumberType ? TkPropertyMessageGet("TKMSG_SCR0043E") : "",
      },
      settlementDate: {
        message: () => {
          if (!formSettlementDate) {
            return TkPropertyMessageGet("TKMSG_SCR0043E");
          }
          const settlementDate = new Date(formSettlementDate);
          if (settlementDate < minDate) {
            return TkPropertyMessageGet("TKMSG_SCR0050E", "1900/01/01");
          }
          if (settlementDate > currentDate) {
            return TkPropertyMessageGet("TKMSG_SCR0051E", "現在");
          }
          return "";
        },
      },
      contractConclusionDate: {
        message: () => {
          if (!formContractConclusionDate) {
            return TkPropertyMessageGet("TKMSG_SCR0043E");
          }
          const contractConclusionDate = new Date(formContractConclusionDate);
          if (contractConclusionDate < minDate) {
            return TkPropertyMessageGet("TKMSG_SCR0050E", "1900/01/01");
          }
          if (contractConclusionDate > maxDate) {
            return TkPropertyMessageGet("TKMSG_SCR0051E", "2099/12/31");
          }
          return "";
        },
      },
      scheduledPaymentDate: {
        message: () => {
          if (!formScheduledPaymentDate) {
            return TkPropertyMessageGet("TKMSG_SCR0043E");
          }
          const scheduledPaymentDate = new Date(formScheduledPaymentDate);
          if (
            !isScheduledPaymentDateDisabled &&
            scheduledPaymentDate < currentDate
          ) {
            return TkPropertyMessageGet("TKMSG_SCR0050E", "現在");
          }
          if (scheduledPaymentDate > maxDate) {
            return TkPropertyMessageGet("TKMSG_SCR0051E", "2099/12/31");
          }
          return "";
        },
      },
      paymentDeadline: {
        message: () => {
          if (paymentDeadlineDisabled) {
            return "";
          }

          if (!formPaymentDeadline) {
            return TkPropertyMessageGet("TKMSG_SCR0043E");
          }
          const paymentDeadline = new Date(formPaymentDeadline);
          if (paymentDeadline < currentDate) {
            return TkPropertyMessageGet("TKMSG_SCR0050E", "現在");
          }
          if (paymentDeadline > maxDate) {
            return TkPropertyMessageGet("TKMSG_SCR0051E", "2099/12/31");
          }
          return "";
        },
      },
    };

    return errorObj;
  };

  /**
  入力フィールドの値が変更されたときのコールバック関数を処理します。
  @param {string} key - 入力フィールドのキー名。
   @returns {Function} 入力フィールドの値が変更されたときのコールバック関数。
  */
  const handleOnChange = (key: string) => (e: any) => {
    const newValue = { ...formValue, [key]: e.target.value };

    if (key === "contractConclusionDate") {
      if (e.target.value?.substring(5) === "04/01") {
        newValue.paymentDeadline = "";
        setPaymentDeadlineDisabled(true);
      } else if (formValue.contractConclusionDate?.substring(5) !== "04/01") {
        setPaymentDeadlineDisabled(false);
      }
    }

    setFormValue(newValue);

    const errorObj = validateForm(newValue, paymentDeadlineDisabled);
    setErrorMsgList({
      symbolNumber: { message: errorObj.symbolNumber.message() },
      symbolNumberType: { message: errorObj.symbolNumberType.message() },
      settlementDate: { message: errorObj.settlementDate.message() },
      contractConclusionDate: {
        message: errorObj.contractConclusionDate.message(),
      },
      scheduledPaymentDate: {
        message: errorObj.scheduledPaymentDate.message(),
      },
      paymentDeadline: { message: errorObj.paymentDeadline.message() },
    });
  };

  /**
    保存ボタンのクリックイベントを処理するコールバック関数です。
    エラーメッセージがなく、成功コールバック関数が存在する場合、成功コールバック関数を呼び出します。
  */
  const handleOnSave = () => {
    const errorObj = validateForm(formValue, paymentDeadlineDisabled);

    if (
      errorObj.symbolNumber.message() ||
      errorObj.settlementDate.message() ||
      errorObj.symbolNumberType.message() ||
      (JobType === "JOB-01" &&
        (errorObj.contractConclusionDate.message() ||
          errorObj.paymentDeadline.message())) ||
      (JobType === "JOB-68" && errorObj.scheduledPaymentDate.message()) ||
      (displayOnlyPaymentDeadline && errorObj.paymentDeadline.message())
    ) {
      setErrorMsgList({
        symbolNumber: { message: errorObj.symbolNumber.message() },
        symbolNumberType: { message: errorObj.symbolNumberType.message() },
        settlementDate: { message: errorObj.settlementDate.message() },
        contractConclusionDate: {
          message: errorObj.contractConclusionDate.message(),
        },
        scheduledPaymentDate: {
          message: errorObj.scheduledPaymentDate.message(),
        },
        paymentDeadline: { message: errorObj.paymentDeadline.message() },
      });
      return;
    }

    onSuccess && onSuccess(formValue);
    clearData();
  };

  const handleOnClose = () => {
    clearData();
    onClose && onClose();
  };

  return (
    <TkModalWithTitle
      open={open}
      title="承認"
      saveButtonName="登録"
      onSave={handleOnSave}
      onClose={handleOnClose}
    >
      <Box className="TkApprovalDateSymbolNumberRegistrationDialog">
        {(JobType === "JOB-01" || displayOnlyPaymentDeadline) && (
          <>
            <Typography className="title">稟議・決裁結果に係る項目</Typography>

            {JobType === "JOB-01" && (
              <TkFakeTableLine
                inputProps={{ "data-testid": "dialogTkFakeTableLine" }}
                title="契約締結日"
                errorsList={errorMsgList}
                keyword="contractConclusionDate"
                requiredIcon
                type="date"
                onChange={handleOnChange("contractConclusionDate")}
                setDefaultValue={formValue.contractConclusionDate}
              ></TkFakeTableLine>
            )}

            {displayOnlyPaymentDeadline && (
              <TkFakeTableLine
                inputProps={{ "data-testid": "dialogTkFakeTableLine" }}
                title="納付期限"
                errorsList={errorMsgList}
                keyword="paymentDeadline"
                requiredIcon
                type="date"
                onChange={handleOnChange("paymentDeadline")}
                disabled={paymentDeadlineDisabled}
                setDefaultValue={formValue.paymentDeadline}
              ></TkFakeTableLine>
            )}
          </>
        )}
        {(JobType === "JOB-68" || isScheduledPaymentDateVisible) && (
          <>
            <Typography className="title">稟議・決裁結果に係る項目</Typography>

            <>
              <TkFakeTableLine
                inputProps={{ "data-testid": "dialogTkFakeTableLine" }}
                title="送金予定日"
                errorsList={errorMsgList}
                keyword="scheduledPaymentDate"
                requiredIcon
                type="date"
                onChange={handleOnChange("scheduledPaymentDate")}
                setDefaultValue={formValue.scheduledPaymentDate}
                disabled={isScheduledPaymentDateDisabled}
              ></TkFakeTableLine>
            </>
          </>
        )}

        <Typography className="title">文書管理に係る項目</Typography>

        <TkFakeTableLine
          title="記号番号"
          errorsList={errorMsgList}
          keyword="symbolNumber"
          requiredIcon
          type="nodeInLine"
          node={
            <Box className="selSec">
              <TkSelectForm
                inputProps={{ "data-testid": "dialogTkSelectForm" }}
                errorMessage={errorMsgList.symbolNumberType?.message}
                error={!!errorMsgList.symbolNumberType?.message}
                className="sel"
                variant="outlined"
                size="small"
                onChange={handleOnChange("symbolNumberType")}
                selectPropsList={Object.keys(CommonOrCommonRetirementFlug).map(
                  (key) => ({
                    label: key,
                    value:
                      CommonOrCommonRetirementFlug[
                        key as keyof typeof CommonOrCommonRetirementFlug
                      ],
                  })
                )}
                wantToSetValue={formValue.symbolNumberType}
              ></TkSelectForm>
              <TkInputWithText
                inputProps={{ "data-testid": "dialogTkInputWithText" }}
                error={!!errorMsgList.symbolNumber?.message}
                helperText={errorMsgList.symbolNumber?.message}
                onChange={handleOnChange("symbolNumber")}
                InputProps={{
                  inputProps: {
                    maxLength: 10,
                  },
                }}
                onlyNumber
                outerTextLeft="第"
                outerTextRight="号"
                wantToSetValue={formValue.symbolNumber}
              />
            </Box>
          }
        ></TkFakeTableLine>

        <TkFakeTableLine
          inputProps={{ "data-testid": "dialogTkFakeTableLine" }}
          title="決裁日"
          errorsList={errorMsgList}
          keyword="settlementDate"
          requiredIcon
          type="date"
          onChange={handleOnChange("settlementDate")}
          setDefaultValue={formValue.settlementDate}
        ></TkFakeTableLine>
      </Box>
    </TkModalWithTitle>
  );
};

export default TkApprovalDateSymbolNumberRegistrationDialog;
